import type {
    ColumnIndexWithScope,
    ExtendedGrid,
    FooterAggregates,
    ShowCheckListEmptyObjectProp,
    ShowCheckListFieldKeysProp
} from '../types/modules/kendo-grid';

function clickedDataItem(e: HTMLElement, grid: kendo.ui.Grid | undefined) {
    const row = $(e).closest('tr');
    return grid?.dataItem(row);
}

function gridEditRowByItemId(grid: kendo.ui.Grid, itemId: number) {
    const dataItem = grid.dataSource.get(itemId);
    const row = grid.tbody.find(`tr[data-uid='${dataItem.uid}']`);
    grid.select(row);
    grid.editRow(row);
}

/**
 * Добавляет в начало грида новую запись с переданными значениями и открывает её редактирование.
 * Как и addRow, запись создаётся на сервере только при сохранении, при отмене грид удаляет её сам
 * */
function gridAddRowWithValues(grid: kendo.ui.Grid, values: object) {
    const dataItem = grid.dataSource.insert(0, values);
    grid.editRow(grid.tbody.find(`tr[data-uid='${dataItem.uid}']`));
}

/**
 * Получаем индекс колонки и скоуп, в котором находятся ее значения
 * */
function getColumnIndexWithScopeByField(grid: kendo.ui.Grid, field: string): ColumnIndexWithScope {
    const column = grid.wrapper.find(`.k-grid-header th[data-field="${field}"]`);

    return {
        columnIndex: column.index(),
        scope: column.closest('.k-grid-header-locked').length ? grid.lockedContent : grid.content
    };
}

function getSelectedRowsData(grid: kendo.ui.Grid, returnType: 'ids' | 'objects') {
    const checkedCheckboxes = grid.element.find('[data-mass-operation] input:checked');
    const items = checkedCheckboxes.map((_, input) => {
        const masterRow = input.closest<HTMLTableRowElement>('tr');
        const uId = masterRow?.dataset.uid ?? '';
        const item = grid.dataSource.getByUid(uId);
        return returnType === 'ids' ? item.get('Id') : item;
    });

    return items.get();
}

function selectedKeys(grid: kendo.ui.Grid) {
    return getSelectedRowsData(grid, 'ids');
}

function getSelectedItems(grid: kendo.ui.Grid) {
    return getSelectedRowsData(grid, 'objects');
}

function initEventCheckboxGridBody(grid: kendo.ui.Grid | kendo.ui.TreeList) {
    const gridElement = grid.element.get(0);
    const editable = grid.options.editable;
    const isMasterRow = (row: HTMLTableRowElement) =>
        !row.classList.contains('k-group-footer') && !row.classList.contains('k-grouping-row');

    const isIgnoredClick = <T extends Element>(target: T): boolean => {
        const guards: Array<(target: T) => boolean> = [
            /**
             * Игнорируем клики по кнопкам в колонках с действиями:
             * "Выгрузить", "Печать" и тд...
             * */
            (target) => Boolean(target.closest('.k-command-cell button')),
            (target) => Boolean(target.closest('a'))
        ];

        return guards.some((x) => x(target));
    };

    grid.element.find('tbody').off('click.massOperation').on('click.massOperation', (e: JQuery.ClickEvent) => {
        const target = e.target;

        if (isIgnoredClick(target)) {
            return;
        }

        const clickedRow = target.closest('tr');
        const lockedContentBody = gridElement?.querySelector<HTMLElement>('.k-grid-content-locked tbody');
        const contentBody = gridElement?.querySelector<HTMLElement>('.k-grid-content tbody');
        const contentMasterRows = [...contentBody?.children ?? []].filter(isMasterRow) as HTMLTableRowElement[];
        const lockedContentMasterRows = [...lockedContentBody?.children ?? []].filter(isMasterRow) as HTMLTableRowElement[];
        const isMassOperationCheckbox = target.closest('input.k-select-checkbox');

        if (clickedRow instanceof HTMLTableRowElement && isMasterRow(clickedRow)) {
            /**
             * Запрещает отрабатывать всем событиям ранее вызванным на tbody.
             * Учитывать, при дальнейшей разработке, что остальные клики на tbody не принесут результатов,
             * текущая функция initEventCheckboxGridBody прерывает все остальные события
             * */
            e.stopImmediatePropagation();

            if (editable && typeof editable === 'object' && 'mode' in editable && editable.mode === 'incell' && !isMassOperationCheckbox) {
                grid.editCell(target);
            }

            const tbody = clickedRow.closest<HTMLElement>('tbody');
            const masterRows = [...tbody?.children ?? []].filter(isMasterRow) as HTMLTableRowElement[];
            const currentRowIndex = masterRows.findIndex((row) => row === clickedRow);
            const checkboxControl = gridElement?.querySelectorAll<HTMLInputElement>('input.k-select-checkbox')[currentRowIndex + 1];

            lockedContentBody && toggleRowSelection(lockedContentMasterRows[currentRowIndex]);
            toggleRowSelection(contentMasterRows[currentRowIndex]);

            if (checkboxControl) {
                const isSelected = clickedRow.classList.contains('k-selected');
                checkboxControl.checked = isSelected;
                checkboxControl.setAttribute('aria-checked', String(isSelected));
            }

            grid.trigger('row:click', { clickedRow, checkboxControl });
            if (gridElement && !!contentMasterRows.length) {
                updateHeaderCheckbox(gridElement, contentMasterRows.length);
            }
        }
    });

    bindMassOperationHeaderCheckbox(grid);
}

function bindMassOperationHeaderCheckbox(grid: kendo.ui.Grid | kendo.ui.TreeList) {
    grid.element.off('change.massOperation', 'thead .k-checkbox').on('change.massOperation', 'thead .k-checkbox', ({ currentTarget }: JQuery.ChangeEvent) => {
        const isChecked = (currentTarget as HTMLInputElement).checked;
        const $tableRows = grid.element.find('tbody').find('tr');
        const $rowCheckbox = $tableRows.find('input[type="checkbox"].k-select-checkbox');

        if (isChecked) {
            $tableRows.addClass('k-state-selected k-selected');
            $rowCheckbox.prop('checked', true).attr('aria-checked', 'true');
        }
        else {
            $tableRows.removeClass('k-state-selected k-selected');
            $rowCheckbox.prop('checked', false).attr('aria-checked', 'false');
        }

        grid.trigger('checkbox:all', { isChecked });
    });
}

function updateHeaderCheckbox(gridElement: HTMLElement, totalMasterRows: number) {
    const headerCheckbox = gridElement.querySelector<HTMLInputElement>('thead .k-checkbox');
    if (!headerCheckbox) {
        return;
    }

    const rowCheckboxes = gridElement.querySelectorAll<HTMLInputElement>(
        '.k-grid-content input.k-select-checkbox, .k-grid-content-locked input.k-select-checkbox'
    );
    const checkedCount = Array.from(rowCheckboxes).filter((cb) => cb.getAttribute('aria-checked') === 'true').length;
    const isAllChecked = checkedCount === totalMasterRows;
    headerCheckbox.checked = isAllChecked;
    headerCheckbox.setAttribute('aria-checked', String(isAllChecked));
}

function toggleRowSelection(row?: HTMLTableRowElement) {
    const isSelected = ['k-selected', 'k-state-selected'].some((x) => row?.classList.contains(x));

    if (isSelected) {
        row?.classList.remove('k-selected', 'k-state-selected');
    }
    else {
        row?.classList.add('k-selected', 'k-state-selected');
    }
}

function initKendoGrid() {
    $('.k-grid').each(function () {
        const element = $(this);
        const grid = element.data('kendoGrid') || element.data('kendoTreeList');

        const autoFitColumn = () => grid?.autoFitColumn(0);
        const autoFitColumnTimeout = () => setTimeout(autoFitColumn);
        const kendoResize = () => setTimeout(() => {
            kendo.resize(element, false);
        });
        const kendoResizeTimeout = () => setTimeout(kendoResize);

        const isTreeList = element.hasClass('k-treelist');
        if (isTreeList) {
            if (element.hasClass('unautoresizable-treelist')) {
                return;
            }

            grid?.bind('expand', autoFitColumnTimeout);
            grid?.bind('collapse', autoFitColumnTimeout);
            grid?.bind('dataBound', autoFitColumnTimeout);
            $('#expandCollapseButtons_btExpandFirstLevel, #expandCollapseButtons_btCollapseFirstLevel').on('click', autoFitColumnTimeout);
            autoFitColumnTimeout();
        }
        else {
            kendoResizeTimeout();
        }

        $(window).on('resize', () => {
            if (isTreeList) {
                autoFitColumn();
            }
            else {
                kendoResize();
            }
        });
    });
}

function headerCheckbox_bindCheckEvent() {
    $('.k-grid:not(.skip-init-event-checkbox-grid-body)').has('thead .k-checkbox').each((_, element) => {
        const $grid = $(element);
        const instance = $grid.data('kendoGrid') || $grid.data('kendoTreeList');

        if (instance) {
            bindMassOperationHeaderCheckbox(instance);
        }
    });
}

function toggleDisablePopupButton(isDisabled: boolean) {
    this.element.find('.k-grid-update').attr('disabled', isDisabled);
}

function showCheckList(urlAction: string, fieldKeys: ShowCheckListFieldKeysProp, emptyObject: ShowCheckListEmptyObjectProp) {
    return new Promise((resolve, reject) => {
        const wrapperId = $('#checkList');
        wrapperId.empty();

        window.ajaxGet(
            urlAction,
            null,
            (result: Record<string, string | number>[]): undefined => {
                $('#tableCheckList').show();
                emptyObject.name = result.length > 0 ? Number(result[0][fieldKeys.id]) : 0;

                for (let i = 0; i < result.length; i++) {
                    wrapperId.append(`
                        <tr class="check_list_data" id="${result[i][fieldKeys.unitId]}" weight="${result[i][fieldKeys.weight]}">
                            <td class="k-master-row">
                                ${result[i][fieldKeys.name]}
                            </td>
                            <td class="k-master-row px-2">
                                <div class="k-input k-input-solid k-rounded-md custom-select">
                                    <select class="k-input-inner check_list_element cursor-pointer" id="${result[i][fieldKeys.unitId]}">
                                        <option selected class="k-list-item"></option>
                                        <option value="true" class="k-list-item">Да</option>
                                        <option value="false" class="k-list-item">Нет</option>
                                    </select>
                                </div>
                            </td>
                        </tr>
                    `);
                }
                resolve(result);
            },
            () => {
                window.standartErrorAnswer();
                reject();
            },
            () => {
                window.lockElement(false);
                reject();
            }
        );
    });
}

function gridRowDblClickEdit(e: JQuery.DoubleClickEvent) {
    const selectedRow = $(e.currentTarget);
    const data = selectedRow.closest('.k-grid').data();
    const [_, key] = Object.keys(data);
    const instance: kendo.ui.Grid | kendo.ui.TreeList = data[key];

    instance.editRow(selectedRow);
}

function gridRowRightClick({ target, which }: JQuery.MouseDownEvent) {
    if (which === 3) {
        const currentElement = $(target).closest('tr[data-uid]');
        const selectedClass = 'k-selected';
        const grid = currentElement.closest('.k-grid').data('kendoGrid');
        const uid = currentElement.attr('data-uid');
        const selectedElements = grid?.element.find(`tr[data-uid="${uid}"]`);

        $(`tr.${selectedClass}`).removeClass(selectedClass);
        currentElement.addClass(selectedClass);
        selectedElements?.addClass(selectedClass);
    }
}

/**
 * В модальном окне редактирования грида меняет название кнопки "Обновить" на "Сохранить"
 * и после обновления библиотеки добавляет классы, которые используются на многих страницах в коде
 * */
function setDefaultSaveButtonText() {
    const localDict = window.getLocaleDict().button;
    const saveWndBtns = document.querySelectorAll('.k-window-actions [ref-update-button], .k-window-actions [data-command="update"]');
    saveWndBtns.forEach((btn) => {
        btn.classList.add('k-grid-update');
        btn.innerHTML = `✔ ${localDict.save}`;
    });

    const cancelWndBtns = document.querySelectorAll('.k-window-actions [ref-cancel-button], .k-window-actions [data-command="cancel"]');
    cancelWndBtns.forEach((btn) => {
        btn.classList.add('k-grid-cancel');
    });
}

function setPopupTitle(e: kendo.ui.GridEditEvent, title: string) {
    e.container?.data('kendoWindow')?.title(title);
}

async function grid_onError(e: kendo.data.DataSourceErrorEvent) {
    const dictionary = window.getLocaleDict();

    if (e.hasOwnProperty('errors') && e.errors) {
        let grid: kendo.ui.Grid | undefined;

        $('.k-grid').each(function () {
            const tmp = $(this).data('kendoGrid');
            if (tmp?.dataSource === e.sender) {
                grid = tmp;
            }
        });

        grid?.one('dataBinding', (ev: kendo.ui.GridDataBindingEvent) => {
            // cancel grid rebind if error occurs
            ev.preventDefault();
        });

        const serverErrorMessages: string[] = [];

        for (const error in e.errors) {
            if (e.errors.hasOwnProperty(error)) {
                serverErrorMessages.push(e.errors[error].errors.toString());
            }
        }

        if (serverErrorMessages.length) {
            void window.swal.fire({
                titleText: dictionary.error,
                html: serverErrorMessages.join('<br>'),
                icon: 'error'
            });
        }
    }
    else if (e.hasOwnProperty('status') && e.status === 'error') {
        await window.swal.fire({ titleText: dictionary.error, html: e.xhr.responseJSON.Message, icon: 'warning' });

        /**
         * Workaround for Kendo UI Grid popup editor: after server-side validation,
         * the Save button keeps the `k-disabled` class and does not become active again.
         * Kendo closed https://github.com/telerik/kendo-ui-core/issues/8402 without a planned fix,
         * so the class is removed manually in our code.
         */
        document.querySelector('.k-window-actions .k-grid-update')?.classList.remove('k-disabled');
    }
}

// Customize the hint
function hintElement(element: JQuery) {
    const grid = element.closest('.k-grid.k-widget').data('kendoGrid');
    const table = grid?.table.clone();
    const wrapperWidth = grid?.wrapper.width() ?? 0;
    const wrapper = $('<div class=\'k-grid k-widget\'></div>').width(wrapperWidth);

    // Remove Grid's header from the hint
    table?.find('thead').remove();
    // Remove the existing rows from the hint
    table?.find('tbody').empty();
    // Remove the existing rows from the hint
    table?.find('tfoot').empty();
    // Wrap the table
    table?.wrap(wrapper);
    // Append the dragged element
    table?.append(element.clone().removeAttr('uid'));
    table?.find('tr').css('background-color', '#a3d0e4');

    // Return the hint element
    return table?.parent();
}

function getVisibleColumns(grid: kendo.ui.Grid, withSettings: boolean = true): string[] {
    const settings = window.getSettings();
    if (settings && withSettings) {
        return settings.columns;
    }
    return grid.columns.filter((c) => !c.hidden).map((c) => c.field).filter(Boolean);
}

function rebindOnChangeColumns(e: kendo.ui.GridColumnMenuInitEvent) {
    compareAndRebindColumns(e, true);
}

function rebindOnChangeColumnsWithoutSettings(e: kendo.ui.GridColumnMenuInitEvent) {
    compareAndRebindColumns(e, false);
}

function compareAndRebindColumns({ sender, container }: kendo.ui.GridColumnMenuInitEvent, withSettings: boolean) {
    const visibleColumns = getVisibleColumns(sender, withSettings);

    container?.data('kendoPopup')?.one('close', () => {
        const actualState = getVisibleColumns(sender, false);
        const isEquals = actualState.length === visibleColumns.length && visibleColumns.every((v, i) => v === actualState[i]);

        if (!isEquals) {
            sender.dataSource.read();
        }
    });
}

function grid_onEdit() {
    setDefaultSaveButtonText();
    window.setControlsSettings();
}

function getState(grid: kendo.ui.Grid | kendo.ui.TreeList) {
    const dataSource = grid.dataSource;
    const columns = grid.columns
        .filter((column: kendo.ui.GridColumn | kendo.ui.TreeListColumn) => !column.hidden)
        .map((c: kendo.ui.GridColumn | kendo.ui.TreeListColumn) => c.field);

    return kendo.stringify({
        sort: dataSource.sort(),
        page: dataSource.page(),
        pageSize: dataSource.pageSize(),
        group: dataSource.group().map((c: { field: string; dir: string }) => ({
            field: c.field,
            dir: c.dir
        })),
        columns: columns
    });
}

function findFirstGrid() {
    const gridElements = $('.k-grid:not(.grid-without-settings)');

    if (!gridElements.length) {
        console.warn('Not found any grids!');
        return null;
    }

    let firstGrid: kendo.ui.Grid | kendo.ui.TreeList | undefined = gridElements.first().data('kendoGrid');

    if (!firstGrid) {
        firstGrid = gridElements.first().data('kendoTreeList');
    }

    if (firstGrid && gridElements.length > 1) {
        console.warn(`Found ${gridElements.length} grid elements! Using first: ${firstGrid.wrapper.attr('id')}`);
    }

    return firstGrid;
}

function filterAggregates(aggregates: CustomAggregateItem[], groups: GridGroupColumn[]) {
    return aggregates.filter(({ aggregate, field, footerTemplate }) =>
        (aggregate !== 'count' || footerTemplate?.includes('count')) || groups.some((group) => group.field === field)
    );
}

/**
 * Поле fields единственное, которое хранит агрегации не тронутыми, но хранит их в таком виде:
 * aggregates: string[], нам же нужно разложить этот массив, в итоге
 * из массива колонок грида нужно получить плоский массив таких элементов:
 * {
 *     field: string;
 *     aggregate: 'count' | 'sum' | 'min' | 'max' | 'average'
 *     footerTemplate?: string
 * }
 * */
function getAggregatesFromFields(fields: GridColumn[] | undefined) {
    return fields?.map((item) => ({ field: item.field, aggregate: item.aggregates, footerTemplate: item.footerTemplate }))
        /**
         * Фильтруем по наличию агрегации в колонке, т.к. есть колонки без агрегаций
         * */
        .filter(({ aggregate }) => aggregate)
        .flatMap(({ field, aggregate, footerTemplate }) =>
            aggregate?.map((type) => ({ field, aggregate: type, footerTemplate }))
        )
        .filter(Boolean);
}

/**
 * Исправление бага с появлением белого прямоугольника на macOS устройствах
 * в области горизонтального scrollbar у Locked контента
 * */
function fixHeightForGridWithLockedContent(instanceGrid?: ExtendedGrid) {
    if (instanceGrid?.lockedContent) {
        instanceGrid.bind('dataBound', ({ sender }: kendo.ui.GridDataBoundEvent) => {
            window.isMacOS() && (
                sender.lockedContent.height(sender.content.height() ?? 0)
            );

            isLimitedHeight(instanceGrid.element) && (
                window.fixLimitedHeightLockedContent(instanceGrid)
            );
        });

        instanceGrid.bind('columnResize', (e: kendo.ui.GridEvent) => {
            isLimitedHeight(instanceGrid.element) && (
                fixLockedContentHeightAtColumnResize(e)
            );
        });
    }
}

function isLimitedHeight(element: JQuery) {
    return Boolean(element.closest('.grid-auto-height.grid-limited-height-500').length);
}

function initMassOperationGrid(sender: kendo.ui.Grid) {
    const hasMassOperationColumn = sender.columns.some((col) => col.attributes?.['data-mass-operation']);

    if (hasMassOperationColumn) {
        initEventCheckboxGridBody(sender);
    }
}

function initMassOperationTreelist(sender: kendo.ui.TreeList) {
    const massOperationColumns = sender.element.find('[data-mass-operation]');

    if (massOperationColumns.length) {
        initEventCheckboxGridBody(sender);
    }
}

function getGridSort(
    settingsSort: GridSortColumn[] | undefined,
    gridColumns: GridColumn[],
    dataSource: PatchedGridDataSource
): kendo.data.DataSourceSortItem[] {
    const [savedSort] = (settingsSort ?? []) as Array<GridSortColumn | undefined>;
    const [currentSort] = (dataSource.sort() ?? []) as Array<kendo.data.DataSourceSortItem | undefined>;

    if (!savedSort) {
        return currentSort ? [currentSort] : [];
    }

    const match = gridColumns.find((c) => c.field === savedSort.field);
    if (!match || !match.sortable) {
        return currentSort ? [currentSort] : [];
    }

    const isDifferent = !currentSort || currentSort.field !== savedSort.field || currentSort.dir !== savedSort.dir;
    return isDifferent ? [savedSort] : [currentSort];
}

function getGridGroups(
    settingsGroup: GridGroupColumn[],
    gridColumns: GridColumn[],
    dataSource: PatchedGridDataSource,
    aggregates: AggregateItem[]
): GridGroupColumn[] {
    const defaultGroups: GridGroupColumn[] = dataSource.group();

    return settingsGroup.reduce<GridGroupColumn[]>((acc, group) => {
        const column = gridColumns.find(({ field }) => field === group.field);

        if (column) {
            const existing = defaultGroups.find(({ field }) => field === group.field);
            const newGroup = existing ?? { ...group, aggregates };

            return [...acc, newGroup];
        }

        return acc;
    }, []);
}

function setSettingsGrid(grid: kendo.ui.Grid | kendo.ui.TreeList, needRebindWithoutSettings: boolean = true) {
    const settings = window.getSettings();

    if (settings) {
        const { page, pageSize, columns, group } = settings;
        const gridColumns: GridColumn[] = grid.columns;
        const dataSource = grid.dataSource as PatchedGridDataSource;
        const isServerAggregates = dataSource.options.serverAggregates;

        const filteredAggregates: AggregateItem[] = isServerAggregates
            ? filterAggregates(getAggregatesFromFields(gridColumns) ?? [], group)
            : dataSource.aggregate();

        setTimeout(() => {
            dataSource.query({
                page,
                pageSize,
                sort: getGridSort(settings.sort, gridColumns, dataSource),
                group: getGridGroups(group, gridColumns, dataSource, filteredAggregates),
                aggregate: filteredAggregates
            });
        }, 3000);

        gridColumns.forEach(({ field }) => {
            const foundColumn = columns.find((column) => column === field);
            grid[!foundColumn && field ? 'hideColumn' : 'showColumn'](field);
        });
    }
    else if (needRebindWithoutSettings) {
        setTimeout(() => grid.dataSource.read(), 500);
    }

    $('[data-role=treelist]').each((_, element) => {
        const instance = $(element).data('kendoTreeList');

        if (!instance?.element.hasClass('skip-init-event-checkbox-grid-body')) {
            instance?.bind('dataBound', ({ sender }: kendo.ui.TreeListDataBoundEvent) => {
                initMassOperationTreelist(sender);
            });
        }
    });

    $('[data-role=grid]').each((_, element) => {
        const instance = $(element).data('kendoGrid') as ExtendedGrid | undefined;

        fixHeightForGridWithLockedContent(instance);

        if (!instance?.element.hasClass('skip-init-event-checkbox-grid-body')) {
            instance?.bind('dataBound', ({ sender }: kendo.ui.GridDataBoundEvent) => {
                initMassOperationGrid(sender);
            });
        }

        if (instance?.dataSource.options.serverAggregates) {
            /**
             * Подписываемся на событие group, чтобы в ручном режиме фильтровать агрегации
             * после запускаем группировку с нужными агрегациями
             * */
            instance.bind('group', (event: GridGroupEvent) => {
                onGroupGridHandle(event, element);
            });

            if (!settings) {
                /**
                 * если на странице нет информации в URL - подписываемся на событие requestStart, отменяем его,
                 * фильтруем агрегации и в ручном режиме запускаем запрос
                 * */
                instance.dataSource.one('requestStart', (event: DataSourceRequestStartEvent) => {
                    event.preventDefault();

                    const group = event.sender.options.group || [] as GridGroupColumn[];
                    const aggregates = getAggregatesFromFields(event.sender.options.fields);
                    event.sender._aggregate = filterAggregates(aggregates ?? [], group);
                    instance.dataSource.read();
                });
            }
        }
    });
}

function onGroupGridHandle(event: GridGroupEvent, nativeElement: HTMLElement) {
    event.preventDefault();

    const { groups, sender } = event;
    /**
     * Проверяем, нужна ли группировка после нажатия на какую то кнопку, или нет
     * Потому что есть страницы, где Антон просил делать такую реализацию
     * */
    const isManualGrouping = nativeElement.hasAttribute('data-manual-grouping');
    const dataSource = sender.dataSource as PatchedGridDataSource;
    /**
     * Берём агрегации из options.dataSource.fields(это массив колонок),
     * т.к. здесь они хранятся в неизмененном виде, но нужно привести это к нашему рабочему виду:
     * */
    const aggregatesFromFields = getAggregatesFromFields(sender.options.dataSource.fields as GridColumn[]);
    const filteredAggregates = filterAggregates(aggregatesFromFields ?? [], groups);
    const updatedGroups = groups.map((group) => ({ ...group, aggregates: filteredAggregates }));

    dataSource._aggregate = filteredAggregates;
    isManualGrouping ? dataSource._group = updatedGroups : dataSource.group(updatedGroups);
}

function configureResizableGrid(instance: kendo.ui.Grid | undefined) {
    if (instance) {
        const gridParent = instance.wrapper.parent();
        const paddingBottom = 10;

        applyGridParentStyles(gridParent, gridParent.offset()?.top ?? 0, paddingBottom);

        $(window).on('resize', () => {
            instance.resize();
            applyGridParentStyles(gridParent, gridParent.offset()?.top ?? 0, paddingBottom);
        });
    }
}

function applyGridParentStyles(grid: JQuery, heightValue: number, paddingBottom: number) {
    grid.css({
        'height': `calc(100vh - ${heightValue}px)`,
        'padding-bottom': `${paddingBottom}px`
    });
}

function changeFooterTemplateForGrid(response: Record<string, number>, grid: ExtendedGrid | undefined, fields: string[]) {
    if (grid?.footerTemplate) {
        const sourceAggregates = grid.dataSource.aggregates();
        const updatedAggregates = JSON.parse(JSON.stringify(sourceAggregates));

        fields.forEach((field) => {
            if (updatedAggregates[field]) {
                const aggregate = updatedAggregates[field];

                if (aggregate.hasOwnProperty('sum')) {
                    aggregate.sum = response[field] ?? '';
                }

                if (aggregate.hasOwnProperty('average')) {
                    aggregate.average = response[field] ?? '';
                }

                if (aggregate.hasOwnProperty('count')) {
                    aggregate.count = response[field] ?? '';
                }
            }
        });

        // Для обновления агрегаций в футере грида у которого есть колонки с locked
        if (grid.element.find('.k-grid-content-locked').length) {
            window.updateLockedFooterTemplate(grid, updatedAggregates);
            return;
        }

        grid.footer.find('.k-footer-template').replaceWith(grid.footerTemplate(updatedAggregates));
    }
}

function updateLockedFooterTemplate(grid: ExtendedGrid, aggregates: FooterAggregates = {}) {
    setTimeout(() => {
        const footerElems = $(grid.footerTemplate(aggregates));
        const allLockedHeaders = grid.element.find('.k-grid-header-locked th');
        const totalLockedCount = allLockedHeaders.length;
        const allFooterCells = footerElems.find('td');
        const allLockedFooterCells = allFooterCells.slice(0, totalLockedCount);
        const unlockedFooterCells = allFooterCells.slice(totalLockedCount);

        const visibleLockedCells = allLockedFooterCells.filter((index) => {
            const correspondingHeader = allLockedHeaders.eq(index);
            return correspondingHeader.is(':visible');
        });

        const unlockedTemplate = $('<tr class="k-footer-template"></tr>').append(unlockedFooterCells);
        const lockedTemplate = $('<tr class="k-footer-template"></tr>').append(visibleLockedCells.clone());

        grid.footer.find('.k-grid-footer-wrap .k-footer-template').replaceWith(unlockedTemplate);
        grid.footer.find('.k-grid-footer-locked .k-footer-template').replaceWith(lockedTemplate);
    });
}

function fixLockedContentHeightAtColumnResize(e: kendo.ui.GridEvent) {
    fixLimitedHeightLockedContent(e.sender as ExtendedGrid);
}

/**
 * Фикс высоты k-grid-content-locked если у грида есть классы
 * grid-auto-height и grid-limited-height-500
 * */
function fixLimitedHeightLockedContent(grid: ExtendedGrid) {
    const lockedContent = grid.element.find('.k-grid-content-locked').get(0);
    if (lockedContent) {
        lockedContent.style.maxHeight = hasHorizontalScrollbar(grid) ? 'calc(500px - 17px)' : '500px';
    }
}

function hasHorizontalScrollbar(grid: ExtendedGrid) {
    const contentElement = grid.element.find('.k-grid-content').get(0);
    return contentElement && (contentElement.scrollWidth > contentElement.clientWidth);
}

/**
 * Фикс колонок в футере, после оптимизации агрегаций в гриде
 * */
function syncFooterColumnsWidth(grid: ExtendedGrid | undefined) {
    const contentTable = grid?.table;
    const footerTable = grid?.footer.find('.k-grid-footer-wrap table');

    if (contentTable && footerTable) {
        const contentCols = contentTable.find('col');
        const footerCols = footerTable.find('col');

        if (contentCols.length === footerCols.length) {
            contentCols.each((index, col) => {
                const contentWidth = col.offsetWidth;
                footerCols.eq(index).css('width', `${contentWidth}px`);
            });
        }
    }
}

window.monthDays = [
    'First',
    'Second',
    'Third',
    'Forth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
    'Eleventh',
    'Twelfth',
    'Thirteenth',
    'Fourteenth',
    'Fifteenth',
    'Sixteenth',
    'Seventeenth',
    'Eighteenth',
    'Nineteenth',
    'Twenty',
    'TwentyFirst',
    'TwentySecond',
    'TwentyThird',
    'TwentyForth',
    'TwentyFifth',
    'TwentySixth',
    'TwentySeventh',
    'TwentyEighth',
    'TwentyNinth',
    'Thirty',
    'ThirtyFirst'
];

function refreshCalendar(groupColumns: GridColumn[], grid: kendo.ui.Grid) {
    const newOptions = Object.assign({}, grid.getOptions());
    newOptions.columns = newOptions.columns?.filter((col) => (
        col.field
        && !window.monthDays.includes(col.field)
        && !col.field.startsWith('day_')
    ));
    newOptions.columns = newOptions.columns?.concat(groupColumns);
    grid.setOptions(newOptions);
}

/**
 * Получение колонки из календаря при двойном клике, для смены статуса
 * */
function getCalendarColumnByCell(td: HTMLTableCellElement, grid: kendo.ui.Grid | undefined) {
    const cellIndex = $(td).parent().children().index(td);
    const allColumns = grid?.options.columns;

    if (allColumns) {
        const countLocked = allColumns.filter((col) => col.locked).length;
        return (cellIndex < 0 || cellIndex >= allColumns.length) ? null : allColumns[cellIndex + countLocked];
    }
}

/**
 * Подставляет в грид автосуммы, переданные с сервера
 * */
function backDataSource_onRequestEnd(e: kendo.data.DataSourceRequestEndEvent, grid: ExtendedGrid | undefined) {
    if (!grid || e.type !== 'read' || !e.response.Total) {
        return;
    }

    const useKendoAutoSums = grid.element.data('useKendoAutoSums') as string | undefined;

    if (useKendoAutoSums && !e.response.AggregateResults) {
        return;
    }

    const aggregates: Record<string, number> = {};
    // прокидываем автосуммы из ответа сервера
    const responseAggregates = (e.response.AggregateResults || []) as AggregateResult[];

    responseAggregates.forEach((aggregate) => {
        aggregates[aggregate.Member] = aggregate.Value;
    });

    // обнуляем все остальные автосуммы
    const gridAggregates = (e.sender?.aggregate()?.map((i: kendo.data.DataSourceAggregateItem) => i.field) || []) as string[];

    gridAggregates.forEach((field) => {
        if (!aggregates.hasOwnProperty(field)) {
            aggregates[field] = 0;
        }
    });

    setTimeout(() => {
        changeFooterTemplateForGrid(aggregates, grid, Object.keys(aggregates));
    });
}

/**
 * Синхронизирует высоту locked строк на основании обычных.
 * Используется, когда высота грида ломается из-за вставки в ячейки комбобоксов, календарей и т.д.
 * */
function syncRowHeights(grid: ExtendedGrid) {
    const contentRows = grid.tbody.find('.k-master-row');
    const lockedRows = grid.lockedTable.find('.k-master-row');

    contentRows.each((index, row) => {
        const rowHeight = row.clientHeight;
        const lockedHeight = lockedRows.get(index)?.clientHeight;

        if (rowHeight && rowHeight !== lockedHeight) {
            lockedRows.eq(index).height(rowHeight);
        }
    });
}

function onColumnMenuCloseUpdateFooter({ sender, container }: kendo.ui.GridColumnMenuInitEvent) {
    container?.data('kendoPopup')?.bind('close', () => {
        updateLockedFooterTemplate(sender as ExtendedGrid, sender.dataSource.aggregates());
    });
}

window.findFirstGrid = findFirstGrid;
window.showCheckList = showCheckList;
window.getState = getState;
window.applyGridParentStyles = applyGridParentStyles;
window.backDataSource_onRequestEnd = backDataSource_onRequestEnd;
window.changeFooterTemplateForGrid = changeFooterTemplateForGrid;
window.compareAndRebindColumns = compareAndRebindColumns;
window.clickedDataItem = clickedDataItem;
window.configureResizableGrid = configureResizableGrid;
window.getCalendarColumnByCell = getCalendarColumnByCell;
window.getColumnIndexWithScopeByField = getColumnIndexWithScopeByField;
window.getSelectedItems = getSelectedItems;
window.getVisibleColumns = getVisibleColumns;
window.grid_onEdit = grid_onEdit;
window.grid_onError = grid_onError;
window.gridEditRowByItemId = gridEditRowByItemId;
window.gridAddRowWithValues = gridAddRowWithValues;
window.gridRowDblClickEdit = gridRowDblClickEdit;
window.gridRowRightClick = gridRowRightClick;
window.setSettingsGrid = setSettingsGrid;
window.hintElement = hintElement;
window.headerCheckbox_bindCheckEvent = headerCheckbox_bindCheckEvent;
window.initKendoGrid = initKendoGrid;
window.onGroupGridHandle = onGroupGridHandle;
window.rebindOnChangeColumns = rebindOnChangeColumns;
window.rebindOnChangeColumnsWithoutSettings = rebindOnChangeColumnsWithoutSettings;
window.refreshCalendar = refreshCalendar;
window.selectedKeys = selectedKeys;
window.setDefaultSaveButtonText = setDefaultSaveButtonText;
window.setPopupTitle = setPopupTitle;
window.toggleDisablePopupButton = toggleDisablePopupButton;
window.toggleRowSelection = toggleRowSelection;
window.updateLockedFooterTemplate = updateLockedFooterTemplate;
window.syncRowHeights = syncRowHeights;
window.fixLimitedHeightLockedContent = fixLimitedHeightLockedContent;
window.syncFooterColumnsWidth = syncFooterColumnsWidth;
window.onColumnMenuCloseUpdateFooter = onColumnMenuCloseUpdateFooter;
window.updateHeaderCheckbox = updateHeaderCheckbox;
